import { useState } from "react";

import BoletoIcon from "@/assets/images/payment-methods/boleto-icon.png";
import CardIcon from "@/assets/images/payment-methods/card-icon.png";
import DefaultIcon from "@/assets/images/payment-methods/default-icon.png";
import PixIcon from "@/assets/images/payment-methods/pix-icon.png";
import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDTypography from "@/components/atoms/MDTypography";
import {
  GroupedOrderPaymentDisplay,
  resolvePaymentIconKey,
} from "@/helpers/order-payment-display.helpers";
import useOrderData from "@/hooks/data/use-orders-data";
import { IOrder } from "@/interfaces/order.interfaces";
import { OrderPaymentMessages } from "@/messages/order-details/order-payment.messages";
import borders from "@/themes/base/borders";

import PaymentDetailsModal from "./PaymentDetailsModal";

type Props = {
  messages: OrderPaymentMessages;
  order: IOrder | null;
  isPrint: boolean;
};

const paymentMethodImages: Record<string, string> = {
  CARTÃO: CardIcon,
  PIX: PixIcon,
  BOLETO: BoletoIcon,
};

function PaymentGroupCard({
  group,
  messages,
  isPrint,
  onOpenDetails,
}: {
  group: GroupedOrderPaymentDisplay;
  messages: OrderPaymentMessages;
  isPrint: boolean;
  onOpenDetails: (group: GroupedOrderPaymentDisplay) => void;
}) {
  const { borderWidth, borderColor } = borders;
  const iconKey = resolvePaymentIconKey(group);
  const paymentMethodImageSrc = paymentMethodImages[iconKey] || DefaultIcon;

  return (
    <>
      <MDBox
        border={`${borderWidth[1]} solid ${borderColor}`}
        borderRadius="lg"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        p={3}
        mt={2}
      >
        <MDBox width="10%" mr={4}>
          <img
            src={paymentMethodImageSrc}
            alt={`${group.paymentMethodName} logo`}
            width={isPrint ? 40 : 60}
            height={isPrint ? 40 : 60}
          />
        </MDBox>

        <MDBox width="100%" mr={2}>
          <MDTypography variant="h6" fontWeight="medium">
            {group.summaryLabel || messages.unknownPaymentMethodMessage}
          </MDTypography>
          <MDTypography variant="subtitle1" fontWeight="regular">
            {group.summaryAmount}
          </MDTypography>
        </MDBox>

        {!isPrint && group.paymentComments ? (
          <MDBox ml="auto" lineHeight={0}>
            <MDButton variant="outlined" color="dark" size="small" onClick={() => onOpenDetails(group)}>
              {messages.paymentDetailsButtonLabel}
            </MDButton>
          </MDBox>
        ) : (
          <MDBox ml="auto" lineHeight={0} />
        )}
      </MDBox>

      {isPrint && group.paymentComments ? (
        <MDBox mt={2}>
          <MDTypography
            variant="body2"
            component="pre"
            sx={{ whiteSpace: "pre-wrap", fontFamily: "inherit", margin: 0 }}
          >
            {group.paymentComments}
          </MDTypography>
        </MDBox>
      ) : null}
    </>
  );
}

export default function PaymentDetails({ messages, order, isPrint }: Props) {
  const { groupedPaymentSummaries } = useOrderData({ order });
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedComments, setSelectedComments] = useState<string | null>(null);

  const handleOpenDetails = (group: GroupedOrderPaymentDisplay) => {
    if (!group.paymentComments) return;
    setSelectedComments(group.paymentComments);
    setDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedComments(null);
  };

  return (
    <>
      <MDTypography variant="h6" fontWeight="medium">
        {messages.paymentInformationTitle}
      </MDTypography>

      {groupedPaymentSummaries.length === 0 ? (
        <MDBox mt={2}>
          <MDTypography variant="subtitle1">{messages.unknownPaymentMethodMessage}</MDTypography>
        </MDBox>
      ) : (
        groupedPaymentSummaries.map((group) => (
          <PaymentGroupCard
            key={group.id}
            group={group}
            messages={messages}
            isPrint={isPrint}
            onOpenDetails={handleOpenDetails}
          />
        ))
      )}

      {selectedComments ? (
        <PaymentDetailsModal
          open={detailsOpen}
          onClose={handleCloseDetails}
          paymentComments={selectedComments}
          messages={messages}
        />
      ) : null}
    </>
  );
}
